import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep, get, set } from 'lodash';
import { Type } from '@lendi/lala-react';
import AuthLoader from '@lendi-ui/auth-loader';

import {
  APPLICANT_ID_PLACEHOLDER,
  APPLICATION_ID_PLACEHOLDER,
  FIELD_PATHS,
  URL_PARAM_APPLICATION_ID,
} from '@funnels/constants';
import { useCustomer, useRouter } from '@funnels/hooks';
import { useAnalytics } from '@funnels/providers';
import { persistApplicationDataCollection } from '@funnels/redux-store';
import { APPLICATION_DATA_COLLECTION_STATUS, ReduxStoreData, StoreApplicant } from '@funnels/types';
import {
  datadogUtil,
  divideGroupedExpenses,
  generateReturnURL,
  getBrandSpecificBaseURL,
  getViewType,
  isRelativeUrl,
  sessionStorageUtil,
} from '@funnels/utils';

import { mapLeadDataToForm } from './util';

function mapState(currentState: ReduxStoreData) {
  const data = {
    applications: currentState.applications.data,
    applicants: currentState.applicants.data,
    assets: currentState.assets.data,
    companies: currentState.companies.data,
    liabilities: currentState.liabilities.data,
    employments: currentState.employments.data,
    expenses: currentState.expenses.data,
    incomes: currentState.incomes.data,
    lead: currentState.lead.data,
  };
  const status = currentState.applicationDataCollection.status;
  return { status, data };
}

export enum PERSIST_STAGE {
  NOT_FIRED,
  APPLICATION_FIRED,
  APPLICANTS_ALL_FIRED,
  REMAINDER_FIRED,
  PRIMARY_APPLICANT_FIRED,
}

const LandingPage: React.FunctionComponent = () => {
  const { query, params } = useRouter();
  const { tracking } = useAnalytics();
  let { returnURL } = query;
  const isHydrated = !!params[URL_PARAM_APPLICATION_ID];

  const dispatch = useDispatch();
  const { status, data } = useSelector(mapState);
  const { applications, applicants, lead } = data;
  const customer = useCustomer();
  const leadData = lead?.data;
  const formValues = leadData
    ? mapLeadDataToForm(leadData, customer.data)
    : sessionStorageUtil.get('funnel_one_form_values');
  if (!returnURL && leadData) {
    returnURL = `${getBrandSpecificBaseURL()}/application/${APPLICATION_ID_PLACEHOLDER}/ctp/${generateReturnURL([
      `${getBrandSpecificBaseURL()}/application/${APPLICATION_ID_PLACEHOLDER}/applicant/${APPLICANT_ID_PLACEHOLDER}/`,
    ])}`;
  }

  const isTeamView = getViewType() === Type.Team;
  const customerId = customer.data.id;
  const isReady = (formValues || leadData) && !customer.isFetching && customerId;
  const history = useHistory();

  React.useEffect(() => {
    if (!isReady) {
      return;
    }
    if (status === APPLICATION_DATA_COLLECTION_STATUS.INIT) {
      const storeData = cloneDeep(formValues);
      const expenses = get(storeData, FIELD_PATHS.EXPENSES(), []);
      set(storeData, FIELD_PATHS.EXPENSES(), divideGroupedExpenses(expenses));
      dispatch(
        persistApplicationDataCollection(storeData, isTeamView, customer.data, tracking!, undefined, isHydrated)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.data]);

  if (status === APPLICATION_DATA_COLLECTION_STATUS.SUCCESS) {
    // We won't hydra on CV landing page so in creating case we will receive create event in reducer and always push the result to redux store
    // but in updating case reducer will mapping the updated value by store value and all store value are empty in the same time
    // so in updating case we need to relay on the form values from the session storage
    const formApplicationId = get(
      formValues,
      FIELD_PATHS.APPLICATION_ID(get(formValues, FIELD_PATHS.APPLICATIONS(), []).length - 1)
    );
    const formApplicants = get(formValues, FIELD_PATHS.APPLICANTS(), []);
    const isUpdate = !!formApplicationId;

    // update form values, so that when user comes back, we know we need to update, not create
    if (!isUpdate) {
      sessionStorageUtil.set('funnel_one_form_values', cloneDeep(data));
    }

    let url = '/';
    if (isRelativeUrl(returnURL)) {
      const applicationId = applications[applications.length - 1]?.id || formApplicationId;
      const redirectApplicant = isUpdate
        ? formApplicants.find((a: StoreApplicant) => a.customerId === customerId) || formApplicants[0]
        : applicants.find((a: StoreApplicant) => a.customerId === customerId) || applicants[0];
      const applicantId = redirectApplicant?.id;

      if (applicationId && applicantId) {
        const regExpApplicationId = new RegExp(APPLICATION_ID_PLACEHOLDER, 'g');
        const regExpApplicantId = new RegExp(APPLICANT_ID_PLACEHOLDER, 'g');

        url = returnURL.replace(regExpApplicationId, applicationId).replace(regExpApplicantId, applicantId);
      }
    } else {
      datadogUtil.addError(`Landing page Error: invalid URL ${returnURL}`);
    }
    window.location.replace(url);

    return null;
  } else if (status === APPLICATION_DATA_COLLECTION_STATUS.ERROR) {
    history.push('/error/');
    return null;
  } else {
    return <AuthLoader />;
  }
};

export default LandingPage;
